import { useState } from 'react';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Table from 'antd/lib/table';
import { ColumnsType } from 'antd/lib/table';
import Spin from 'antd/lib/spin';
import Tag from 'antd/lib/tag';
import { PjmRegistrationDto } from 'src/domain/pjm/dto/PjmRegistrationDto';
import { PjmRegistrationStatus } from 'src/domain/pjm/dto/enums';
import { usePjmRegistrationSubmitMutation } from 'src/domain/pjm/queries';
import { isRegistrationActionAllowed } from '../../isRegistrationActionAllowed';
import Tooltip from 'antd/lib/tooltip';
import WarningFilled from '@ant-design/icons/WarningFilled';
import Alert from 'antd/lib/alert';
import { Space } from 'antd';

type SubmitStatus = 'ready to submit' | 'cannot be submitted' | 'already submitted' | 'error';

interface RegistrationRow {
    isLoading: boolean;
    submitError: string | null;
    uuid: string;
    name: string;
    id?: number;
    status?: PjmRegistrationStatus;
    submitStatus: SubmitStatus;
}

function getInitialSubmitStatus(registration: PjmRegistrationDto): SubmitStatus {
    if (registration.summaryInfo?.status === PjmRegistrationStatus.PENDING_PJM) {
        return 'already submitted';
    }
    if (isRegistrationActionAllowed(registration, 'submit')) {
        return 'ready to submit';
    }
    return 'cannot be submitted';
}

const STATUS_COLOR: Record<SubmitStatus, string> = {
    'ready to submit': 'green',
    'cannot be submitted': 'default',
    'already submitted': 'blue',
    error: 'red',
};

interface IPjmRegistrationsBulkSubmitModalProps {
    registrations: PjmRegistrationDto[];
    onClose: (actionTaken: boolean) => void;
}

export const PjmRegistrationsBulkSubmitModal = ({ registrations, onClose }: IPjmRegistrationsBulkSubmitModalProps) => {
    const [rows, setRows] = useState<RegistrationRow[]>(() =>
        registrations
            .map(reg => ({
                isLoading: false,
                submitError: null,
                uuid: reg.uuid,
                name: reg.summaryInfo?.name,
                id: reg.id,
                status: reg.summaryInfo.status,
                submitStatus: getInitialSubmitStatus(reg),
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
    );

    const [isSubmitting, setIsSubmitting] = useState(false);

    const pjmRegistrationSubmitMutation = usePjmRegistrationSubmitMutation();

    const readyToSubmitRows = rows.filter(r => r.submitStatus === 'ready to submit');
    const canSubmit = readyToSubmitRows.length > 0;

    const updateRow = (uuid: string, dto: { isLoading?: boolean; submitStatus?: SubmitStatus; submitError?: string | null }) => {
        setRows(prev => prev.map(r => (r.uuid === uuid ? { ...r, ...dto } : r)));
    };

    async function handleSubmit() {
        setIsSubmitting(true);
        let someFail = false;

        for (const row of readyToSubmitRows) {
            updateRow(row.uuid, { isLoading: true });
            try {
                await pjmRegistrationSubmitMutation.mutateAsync({ uuid: row.uuid });
                updateRow(row.uuid, { submitStatus: 'already submitted' });
            } catch (err) {
                someFail = true;
                updateRow(row.uuid, { submitStatus: 'error', submitError: (err as Error)?.message });
            } finally {
                updateRow(row.uuid, { isLoading: false });
            }
        }

        setIsSubmitting(false);
        if (someFail === false) {
            onClose(true);
        }
    }

    const columns: ColumnsType<RegistrationRow> = [
        {
            title: 'Name',
            dataIndex: 'name',
        },
        {
            title: 'ID',
            dataIndex: 'id',
            render: (value: number | undefined) => value ?? '—',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (value: string | undefined) => value ?? '—',
        },
        {
            title: 'Submit Status',
            dataIndex: 'submitStatus',
            render: (status: SubmitStatus, record) => (
                <span>
                    <Tag color={STATUS_COLOR[status]}>{status}</Tag>
                    {record.isLoading && <Spin size="small" style={{ marginRight: 8 }} />}
                    {record.submitError && (
                        <Tooltip title={record.submitError}>
                            <WarningFilled style={{ fontSize: '14px', color: 'orange', cursor: 'pointer' }} />
                        </Tooltip>
                    )}
                </span>
            ),
        },
    ];

    return (
        <Modal
            title="Bulk Submit PJM Registrations"
            open
            width={700}
            destroyOnHidden
            maskClosable={false}
            keyboard={false}
            onCancel={() => onClose(false)}
            footer={[
                <Button key="close" onClick={() => onClose(false)} disabled={isSubmitting}>
                    Close
                </Button>,
                <Button key="submit" type="primary" disabled={!canSubmit} loading={isSubmitting} onClick={handleSubmit}>
                    Submit
                </Button>,
            ]}
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                    message="Information"
                    description="Please do not close this dialog box until all registrations have been submitted."
                    type="info"
                    showIcon
                />
                <Table<RegistrationRow>
                    size="small"
                    rowKey="uuid"
                    columns={columns}
                    dataSource={rows}
                    pagination={{
                        pageSize: 10,
                        total: rows.length,
                        showSizeChanger: false,
                    }}
                />
            </Space>
        </Modal>
    );
};
